import React, { useEffect, useRef, useState } from 'react';
import globalImageCache from './globalImageCache';
import '../../styles/contents/shortFormQuiz/ShortFormQuizRobotFactory.css';

// 피드백 이미지들 (public 폴더 경로)
const bubbleSuccessUrl = '/assets/images/contents/shortForm/bubble/Bubble_Success.png';
const bubbleFailUrl = '/assets/images/contents/shortForm/bubble/Bubble_Fail.png';

// 빈 로봇 파츠 이미지들 URL (public 폴더의 이미지들은 런타임에 접근)
const robotArrowUrl = '/assets/images/contents/shortForm/RobotArrow.png';
const robotBodyEmptyUrl = '/assets/images/contents/shortForm/RobotBodyEmpty.png';
const robotHeadEmptyUrl = '/assets/images/contents/shortForm/RobotHeadEmpty.png';
const robotLegEmptyUrl = '/assets/images/contents/shortForm/RobotLegEmpty.png';
// 실제 로봇 파츠 이미지
const robotBodyUrl = '/assets/images/contents/shortForm/RobotBody.png';
const robotHeadUrl = '/assets/images/contents/shortForm/RobotHead.png';
const robotLegUrl = '/assets/images/contents/shortForm/RobotLeg.png';
// 퀴즈용 몸체 이미지
const robotBodyQuizUrl = '/assets/images/contents/shortForm/RobotBodyQuiz.png';

const ShortFormQuizRobotFactory = ({ 
  stagePhase, 
  localStage, 
  quiz, 
  setStagePhase, 
  correctCountRef, 
  totalQuestionsRef, 
  answersRef, 
  wrongIndexesRef, 
  stageAnsweredRef,
  feedbackActive,
  feedbackIsSuccess,
  feedbackRect,
  setFeedbackActive,
  setFeedbackIsSuccess,
  setFeedbackRect,
  setIsGameInProgress,
  successImageUrl,
  failImageUrl,
  // 새로 추가된 props
  includeTutorial,
  isTutorialStage
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const imagesRef = useRef({ 
    success: null, 
    fail: null, 
    robotArrow: null,
    robotBody: null,
    robotHead: null,
    robotLeg: null,
    robotBodyEmpty: null,
    robotHeadEmpty: null,
    robotLegEmpty: null,
    robotBodyQuiz: null,
    ready: false 
  });

  // 튜토리얼 모드용 상태 관리
  const [tutorialState, setTutorialState] = useState({
    // 중앙 슬롯들의 상태 (head: 0, body: 1, leg: 2)
    // 0: 빈 슬롯, 1: 머리, 2: 몸체, 3: 다리
    centerSlots: [0, 0, 0], 
    // 튜토리얼에서 어떤 슬롯이 비어있어야 하는지 (랜덤으로 선택)
    targetEmptySlot: Math.floor(Math.random() * 3), // 0, 1, 2 중 하나
    // 드래그 상태
    draggedPart: null, // 현재 드래그 중인 파츠 (1: 머리, 2: 몸체, 3: 다리)
    dragOffset: { x: 0, y: 0 },
    isDragging: false,
    // 하단 파츠들의 위치 (드래그로 인한 이동 추적)
    bottomParts: [
      { type: 1, x: 0, y: 0, visible: true }, // 머리
      { type: 2, x: 0, y: 0, visible: true }, // 몸체  
      { type: 3, x: 0, y: 0, visible: true }  // 다리
    ]
  });

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // 퀴즈 모드용 상태 관리 (localStage >= 2)
  const [quizState, setQuizState] = useState({
    // 중앙 슬롯들의 상태 (머리: 1, 몸체: 0(빈자리), 다리: 3)
    centerSlots: [1, 0, 3], // 머리와 다리는 채워진 상태, 몸체만 빈자리
    // 퀴즈 선택지들
    options: [], // 4개의 선택지 텍스트
    correctIndex: 0, // 정답 인덱스
    selectedIndex: null, // 선택된 인덱스
    // 드래그 상태
    draggedOption: null, // 현재 드래그 중인 선택지 인덱스 (0~3)
    dragOffset: { x: 0, y: 0 },
    isDragging: false
  });

  // 전역 캐시에서 이미지 가져오기
  useEffect(() => {
    if (globalImageCache.isReadyToUse()) {
      // 전역 캐시에서 이미지 가져오기
      const success = globalImageCache.get(bubbleSuccessUrl);
      const fail = globalImageCache.get(bubbleFailUrl);
      const robotArrow = globalImageCache.get(robotArrowUrl);
      const robotBodyEmpty = globalImageCache.get(robotBodyEmptyUrl);
      const robotHeadEmpty = globalImageCache.get(robotHeadEmptyUrl);
      const robotLegEmpty = globalImageCache.get(robotLegEmptyUrl);
      const robotBody = globalImageCache.get(robotBodyUrl);
      const robotHead = globalImageCache.get(robotHeadUrl);
      const robotLeg = globalImageCache.get(robotLegUrl);
      const robotBodyQuiz = globalImageCache.get(robotBodyQuizUrl);
      
      imagesRef.current = { 
        success, 
        fail, 
        robotArrow,
        robotBodyEmpty,
        robotHeadEmpty,
        robotLegEmpty,
        robotBody,
        robotHead,
        robotLeg,
        robotBodyQuiz,
        ready: true 
      };
      
    } else {
      // 전역 캐시가 준비되지 않았으면 기존 방식으로 로딩
      
      // 이미지 객체들 직접 생성 (fallback)
      const success = new Image(); success.src = bubbleSuccessUrl;
      const fail = new Image(); fail.src = bubbleFailUrl;
      const robotArrow = new Image(); robotArrow.src = robotArrowUrl;
      const robotBodyEmpty = new Image(); robotBodyEmpty.src = robotBodyEmptyUrl;
      const robotHeadEmpty = new Image(); robotHeadEmpty.src = robotHeadEmptyUrl;
      const robotLegEmpty = new Image(); robotLegEmpty.src = robotLegEmptyUrl;
      const robotBody = new Image(); robotBody.src = robotBodyUrl;
      const robotHead = new Image(); robotHead.src = robotHeadUrl;
      const robotLeg = new Image(); robotLeg.src = robotLegUrl;
      const robotBodyQuiz = new Image(); robotBodyQuiz.src = robotBodyQuizUrl;

      let loaded = 0;
      const totalImages = 10;
      
      const onLoaded = () => { 
        loaded += 1; 
        if (loaded >= totalImages) { 
          imagesRef.current.ready = true;
        } 
      };
      
      const onErr = (src) => { 
        console.error(`로봇 팩토리 이미지 로딩 실패: ${src}`); 
        onLoaded(); // 에러도 완료로 카운트
      };
      
      [success, fail, robotArrow, robotBodyEmpty, robotHeadEmpty, robotLegEmpty, robotBody, robotHead, robotLeg, robotBodyQuiz].forEach((img) => { 
        img.onload = onLoaded; 
        img.onerror = () => onErr(img.src); 
      });
      
      imagesRef.current = { 
        success, 
        fail, 
        robotArrow,
        robotBodyEmpty,
        robotHeadEmpty,
        robotLegEmpty,
        robotBody,
        robotHead,
        robotLeg,
        robotBodyQuiz,
        ready: false 
      };

      // 클린업
      return () => {
        [success, fail, robotArrow, robotBodyEmpty, robotHeadEmpty, robotLegEmpty, robotBody, robotHead, robotLeg, robotBodyQuiz].forEach((img) => { 
          img.onload = null; 
          img.onerror = null; 
        });
      };
    }
  }, [globalImageCache]);

  // 튜토리얼 초기화 (isTutorialStage일 때)
  useEffect(() => {
    if (isTutorialStage && stagePhase === 'play') {
      // 초기 중앙 슬롯 설정: 타겟 빈 슬롯을 제외한 나머지 2개는 올바른 파츠로 채우기
      setTutorialState(prev => {
        const newCenterSlots = [1, 2, 3]; // 머리, 몸체, 다리
        const targetEmpty = prev.targetEmptySlot;
        newCenterSlots[targetEmpty] = 0; // 타겟 슬롯을 빈 상태로
        
        return {
          ...prev,
          centerSlots: newCenterSlots
        };
      });
    }
  }, [isTutorialStage, stagePhase]); // targetEmptySlot 의존성 제거

  // 퀴즈 초기화 (!isTutorialStage일 때, 실제 퀴즈 모드)
  useEffect(() => {
    if (!isTutorialStage && stagePhase === 'play') {
      // 퀴즈 데이터에서 선택지와 정답 추출 - 수정된 인덱스 계산
      const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
      let qIndexRaw;
      
      if (includeTutorial) {
        qIndexRaw = localStage - 5; // localStage 5 = qIndex 0
      } else {
        qIndexRaw = localStage - 1; // localStage 1 = qIndex 0
      }
      
      let options = ['A', 'B', 'C', 'D'];
      let correctIndex = 0;
      
      // 유효한 문제 범위 내에서만 처리
      if (qList.length > 0 && qIndexRaw >= 0 && qIndexRaw < qList.length) {
        const q = qList[qIndexRaw];
        if (q && Array.isArray(q.options)) {
          options = q.options.slice(0, 4).map(String); // 최대 4개 선택지
          // 4개 미만이면 나머지는 기본값으로 채우기
          while (options.length < 4) {
            options.push(`선택지 ${options.length + 1}`);
          }
        }
        if (typeof q?.answerIndex === 'number') {
          correctIndex = Math.min(q.answerIndex, 3); // 0~3 범위로 제한
        }
      }
      
      setQuizState(prev => ({
        ...prev,
        centerSlots: [1, 0, 3], // 머리와 다리는 채워진 상태, 몸체만 빈자리
        options: options,
        correctIndex: correctIndex,
        selectedIndex: null,
        draggedOption: null,
        isDragging: false
      }));
    }
  }, [isTutorialStage, stagePhase, quiz?.questions, localStage, includeTutorial]);

  // 마우스/터치 이벤트 헬퍼 함수들
  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if (e.touches && e.touches.length > 0) {
      // 터치 이벤트 (touchstart, touchmove)
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // 터치 종료 이벤트 (touchend) - changedTouches 사용
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      // 마우스 이벤트
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  // 점이 사각형 안에 있는지 확인
  const isPointInRect = (px, py, rx, ry, rw, rh) => {
    return px >= rx - rw/2 && px <= rx + rw/2 && py >= ry - rh/2 && py <= ry + rh/2;
  };

  // 하단 파츠 클릭/터치 감지 (튜토리얼 모드)
  const getClickedBottomPart = (x, y, canvas) => {
    if (!canvas || !imagesRef.current.ready) return null;
    
    const maxPartSize = Math.min(canvas.width, canvas.height) * 0.15; // 크기 업데이트
    const bottomY = canvas.height * 0.82;
    const centerX = canvas.width / 2;
    const horizontalSpacing = maxPartSize * 2.0; // 튜토리얼 렌더링과 동일한 간격
    
    const parts = [
      { type: 1, x: centerX - horizontalSpacing, y: bottomY }, // 머리
      { type: 2, x: centerX, y: bottomY },                    // 몸체
      { type: 3, x: centerX + horizontalSpacing, y: bottomY } // 다리
    ];
    
    for (const part of parts) {
      if (isPointInRect(x, y, part.x, part.y, maxPartSize, maxPartSize)) {
        // 해당 파츠가 아직 드래그 가능한지 확인 (타겟 빈 슬롯과 같은 타입인지)
        const targetSlot = tutorialState.targetEmptySlot;
        const requiredPartType = targetSlot + 1; // 0->1(머리), 1->2(몸체), 2->3(다리)
        
        if (part.type === requiredPartType) {
          return part;
        }
      }
    }
    
    return null;
  };

  // 하단 퀴즈 선택지 클릭/터치 감지 (퀴즈 모드)
  const getClickedQuizOption = (x, y, canvas) => {
    if (!canvas || !imagesRef.current.ready || isTutorialStage) return null;
    
    const maxPartSize = Math.min(canvas.width, canvas.height) * 0.15; // 크기 업데이트
    const bottomAreaY = canvas.height * 0.68; // 하단 영역 시작점 (위로 올림)
    const optionSize = maxPartSize * 1.5; // 선택지 크기 (렌더링과 동일)
    const horizontalSpacing = maxPartSize * 2.2; // 가로 간격 (렌더링과 동일)
    const verticalSpacing = horizontalSpacing * 0.6; // 세로 간격 (렌더링과 동일)
    
    // 2x2 격자 배치 - 세로 간격을 가로 간격의 50%로 설정
    const centerX = canvas.width / 2;
    const positions = [
      { x: centerX - horizontalSpacing/2, y: bottomAreaY, index: 0 },                            // 왼쪽 상단
      { x: centerX + horizontalSpacing/2, y: bottomAreaY, index: 1 },                            // 오른쪽 상단
      { x: centerX - horizontalSpacing/2, y: bottomAreaY + verticalSpacing, index: 2 },          // 왼쪽 하단
      { x: centerX + horizontalSpacing/2, y: bottomAreaY + verticalSpacing, index: 3 }           // 오른쪽 하단
    ];
    
    for (const pos of positions) {
      if (isPointInRect(x, y, pos.x, pos.y, optionSize, optionSize)) {
        return { index: pos.index, x: pos.x, y: pos.y };
      }
    }
    
    return null;
  };

  // 중앙 슬롯 드롭 감지
  const getDropSlot = (x, y, canvas) => {
    if (!canvas || !imagesRef.current.ready) return null;
    
    const maxPartSize = Math.min(canvas.width, canvas.height) * 0.15; // 크기 업데이트
    const gameAreaTop = canvas.height * 0.20; // 수정된 위치 반영
    const gameAreaBottom = canvas.height * 0.65; // 수정된 위치 반영
    const gameAreaHeight = gameAreaBottom - gameAreaTop;
    const gameCenterY = gameAreaTop + gameAreaHeight / 2;
    const centerX = canvas.width / 2;
    const verticalSpacing = maxPartSize * 0.8; // 수정된 간격 반영
    
    const slots = [
      { index: 0, x: centerX, y: gameCenterY - verticalSpacing }, // 머리 슬롯
      { index: 1, x: centerX, y: gameCenterY },                   // 몸체 슬롯
      { index: 2, x: centerX, y: gameCenterY + verticalSpacing }  // 다리 슬롯
    ];
    
    // 드롭 영역을 50% 더 크게 만들어서 모바일에서 더 쉽게 드롭할 수 있도록 함
    const dropAreaSize = maxPartSize * 1.5;
    
    for (const slot of slots) {
      if (isPointInRect(x, y, slot.x, slot.y, dropAreaSize, dropAreaSize)) {
        return slot.index;
      }
    }
    
    return null;
  };

  // 퀴즈 모드용 몸체 슬롯 드롭 감지 (몸체 슬롯만 체크)
  const getQuizBodySlotDrop = (x, y, canvas) => {
    if (!canvas || !imagesRef.current.ready || isTutorialStage) return false;
    
    const maxPartSize = Math.min(canvas.width, canvas.height) * 0.15; // 크기 업데이트
    const gameAreaTop = canvas.height * 0.20; // 수정된 위치 반영
    const gameAreaBottom = canvas.height * 0.65; // 수정된 위치 반영
    const gameAreaHeight = gameAreaBottom - gameAreaTop;
    const gameCenterY = gameAreaTop + gameAreaHeight / 2;
    const centerX = canvas.width / 2;
    
    // 몸체 슬롯 (index 1)만 체크 - 몸체 크기 반영하고 드롭 영역을 더 크게
    const bodySlotSize = maxPartSize * 2.0; // 몸체는 100% 크게 (기존 50%에서 증가)
    return isPointInRect(x, y, centerX, gameCenterY, bodySlotSize, bodySlotSize);
  };

  // 마우스 다운/터치 시작 이벤트
  const handlePointerDown = (e) => {
    if (stagePhase !== 'play') return;
    
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPos(e);
    const canvas = canvasRef.current;
    
    if (isTutorialStage) {
      // 튜토리얼 모드
      const clickedPart = getClickedBottomPart(pos.x, pos.y, canvas);
      
      if (clickedPart) {
        setTutorialState(prev => ({
          ...prev,
          draggedPart: clickedPart.type,
          dragOffset: { x: pos.x - clickedPart.x, y: pos.y - clickedPart.y },
          isDragging: true
        }));
        setMousePos(pos);
      }
    } else {
      // 퀴즈 모드 - 몸체 슬롯이 비어있을 때만 드래그 가능
      if (quizState.centerSlots[1] === 0) {
        const clickedOption = getClickedQuizOption(pos.x, pos.y, canvas);
        
        if (clickedOption) {
          setQuizState(prev => ({
            ...prev,
            draggedOption: clickedOption.index,
            dragOffset: { x: pos.x - clickedOption.x, y: pos.y - clickedOption.y },
            isDragging: true
          }));
          setMousePos(pos);
        }
      }
    }
  };

  // 마우스 이동/터치 이동 이벤트
  const handlePointerMove = (e) => {
    if (stagePhase !== 'play') return;
    
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPos(e);
    setMousePos(pos);
  };

  // 마우스 업/터치 종료 이벤트
  const handlePointerUp = (e) => {
    if (stagePhase !== 'play') return;
    
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPos(e);
    const canvas = canvasRef.current;
    
    if (isTutorialStage && tutorialState.isDragging) {
      // 튜토리얼 모드
      const dropSlot = getDropSlot(pos.x, pos.y, canvas);
      
      // 드래그한 파츠가 해당 슬롯에 맞는지 확인
      // 파츠 타입: 1=머리, 2=몸체, 3=다리
      // 슬롯 인덱스: 0=머리, 1=몸체, 2=다리
      const requiredPartType = dropSlot + 1; // 슬롯 인덱스를 파츠 타입으로 변환
      
      if (dropSlot !== null && 
          dropSlot === tutorialState.targetEmptySlot && 
          tutorialState.draggedPart === requiredPartType) {
        // 올바른 슬롯에 올바른 파츠를 드롭됨
        const newCenterSlots = [...tutorialState.centerSlots];
        newCenterSlots[dropSlot] = tutorialState.draggedPart;
        
        setTutorialState(prev => ({
          ...prev,
          centerSlots: newCenterSlots,
          draggedPart: null,
          isDragging: false
        }));
        
        // 게임 완료 체크
        const allFilled = newCenterSlots.every(slot => slot !== 0);
        if (allFilled) {
          // 답안 기록 (튜토리얼은 항상 성공)
          if (!stageAnsweredRef.current) {
            correctCountRef.current += 1;
            if (!Array.isArray(answersRef.current)) answersRef.current = [];
            answersRef.current[0] = 0; // 튜토리얼 답안
            stageAnsweredRef.current = true;
            if (setIsGameInProgress) setIsGameInProgress(false);
          }
          
          // 튜토리얼 성공!
          setTimeout(() => {
            const cx = canvas?.width / 2 || 300;
            const cy = canvas?.height / 2 || 200;
            setFeedbackActive(true);
            setFeedbackIsSuccess(true);
            const base = Math.min(canvas?.width || 600, canvas?.height || 400) * 0.3;
            setFeedbackRect({ x: cx, y: cy, w: base, h: base });
            setStagePhase('feedback');
          }, 300);
        }
      } else {
        // 잘못된 곳에 드롭되거나 슬롯 밖에 드롭됨, 또는 잘못된 파츠 타입
        setTutorialState(prev => ({
          ...prev,
          draggedPart: null,
          isDragging: false
        }));
      }
    } else if (!isTutorialStage && quizState.isDragging) {
      // 퀴즈 모드
      const isBodySlotDrop = getQuizBodySlotDrop(pos.x, pos.y, canvas);
      
      if (isBodySlotDrop) {
        // 몸체 슬롯에 드롭됨 - 정답/오답 체크
        const selectedIndex = quizState.draggedOption;
        const isCorrect = selectedIndex === quizState.correctIndex;
        
        // 답안 기록
        if (!stageAnsweredRef.current) {
          if (isCorrect) {
            correctCountRef.current += 1;
          } else {
            // 수정된 인덱스 계산으로 wrongIndexes 기록
            let qIndexRaw;
            if (includeTutorial) {
              qIndexRaw = localStage - 5;
            } else {
              qIndexRaw = localStage - 1;
            }
            
            const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
            if (qIndexRaw >= 0 && qIndexRaw < qList.length) {
              const oneBased = qIndexRaw + 1;
              if (!wrongIndexesRef.current.includes(oneBased)) {
                wrongIndexesRef.current.push(oneBased);
              }
            }
          }
          
          // 답안 기록 - 수정된 인덱스 계산
          if (!Array.isArray(answersRef.current)) answersRef.current = [];
          let qIndexRaw2;
          if (includeTutorial) {
            qIndexRaw2 = localStage - 5;
          } else {
            qIndexRaw2 = localStage - 1;
          }
          
          const qList2 = Array.isArray(quiz?.questions) ? quiz.questions : [];
          if (qIndexRaw2 >= 0 && qIndexRaw2 < qList2.length) {
            answersRef.current[qIndexRaw2] = selectedIndex;
          }
          stageAnsweredRef.current = true;
          
          // 게임 진행 상태 해제
          if (setIsGameInProgress) setIsGameInProgress(false);
        }
        
        // 몸체 슬롯을 선택된 파츠로 채우기
        const newCenterSlots = [...quizState.centerSlots];
        newCenterSlots[1] = 2; // 몸체 파츠로 채우기 (2 = 몸체)
        
        setQuizState(prev => ({
          ...prev,
          centerSlots: newCenterSlots,
          selectedIndex: selectedIndex,
          draggedOption: null,
          isDragging: false
        }));
        
        // 2초간 테두리 표시 후 피드백 이미지 표시
        setTimeout(() => {
          const cx = canvas?.width / 2 || 300;
          const cy = canvas?.height / 2 || 200;
          setFeedbackActive(true);
          setFeedbackIsSuccess(isCorrect);
          const base = Math.min(canvas?.width || 600, canvas?.height || 400) * 0.3;
          setFeedbackRect({ x: cx, y: cy, w: base, h: base });
          setStagePhase('feedback');
        }, 2000); // 300ms -> 2000ms로 변경
      } else {
        // 잘못된 곳에 드롭됨
        setQuizState(prev => ({
          ...prev,
          draggedOption: null,
          isDragging: false
        }));
      }
    }
  };

  // 게임 초기화 및 렌더링
  useEffect(() => {
    if (stagePhase !== 'pre' && stagePhase !== 'play' && stagePhase !== 'feedback') {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    if (!parent) return;
    
    // StudentPage의 레이아웃에 맞는 크기 계산
    // 상단 UI 영역(20%)을 제외한 나머지 80% 영역을 완전히 사용
    const parentRect = parent.getBoundingClientRect();
    const availableWidth = parentRect.width;
    const availableHeight = parentRect.height * 0.8; // 상단 UI 20% 제외
    
    // 사용 가능한 영역을 완전히 채우도록 설정
    const width = availableWidth;
    const height = availableHeight;
    
    // 캔버스 크기 설정
    if (stagePhase === 'pre' || stagePhase === 'play') {
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
    }

    // 렌더링 루프
    const ctx = canvas.getContext('2d');
    
    const draw = () => {
      // 배경을 로봇 팩토리 테마 색상으로 설정 (#237BFF)
      ctx.fillStyle = '#237BFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 제목 텍스트와 로봇 파츠 표시
      if (stagePhase === 'play' || stagePhase === 'pre') {
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        
        // 제목 텍스트 제거됨
        
        // 로봇 파츠들과 화살표 그리기 (이미지가 로딩된 경우)
        if (imagesRef.current.ready) {
          // 로봇 파츠들의 기본 크기 계산 (비율 유지를 위한 최대 크기) - 50% 증가
          const maxPartSize = Math.min(canvas.width, canvas.height) * 0.15;
          // 화살표 크기 50% 크게
          const arrowSize = Math.min(canvas.width, canvas.height) * 0.06;
          
          // 게임 영역의 실제 중앙 계산 (제목 제외) - 위치를 더 위로 올림
          const gameAreaTop = canvas.height * 0.20; // 제목 아래부터 (25% -> 20%)
          const gameAreaBottom = canvas.height * 0.65; // 하단 여백 늘림 (90% -> 65%)
          const gameAreaHeight = gameAreaBottom - gameAreaTop;
          const gameCenterY = gameAreaTop + gameAreaHeight / 2;
          
          // 중앙 파츠들 - 게임 영역에 세로로 정렬 (간격 줄임)
          const centerX_parts = centerX; // 화면 정중앙
          const verticalSpacing = maxPartSize * 0.8; // 파츠 간 세로 간격 (1.4 -> 0.8)
          
          const headSlotX = centerX_parts;
          const headSlotY = gameCenterY - verticalSpacing; // 게임 영역 중앙 위
          
          const bodySlotX = centerX_parts;
          const bodySlotY = gameCenterY; // 게임 영역 정중앙
          
          const legSlotX = centerX_parts;
          const legSlotY = gameCenterY + verticalSpacing; // 게임 영역 중앙 아래
          
          // 화살표들: 파츠들 오른쪽에 세로로 배치 (더 멀리)
          const arrowOffsetX = maxPartSize * 1.2; // 파츠 오른쪽으로 이동 (0.8 -> 1.2)
          const arrowOffsetY = 0; // 같은 높이에 배치
          
          // 이미지 비율을 유지하여 그리는 헬퍼 함수
          const drawImageWithAspectRatio = (img, x, y, maxSize, opacity = 1) => {
            if (!img || !img.complete) return;
            
            const aspectRatio = img.width / img.height;
            let drawWidth, drawHeight;
            
            if (aspectRatio > 1) {
              // 가로가 더 긴 경우
              drawWidth = maxSize;
              drawHeight = maxSize / aspectRatio;
            } else {
              // 세로가 더 긴 경우 또는 정사각형
              drawHeight = maxSize;
              drawWidth = maxSize * aspectRatio;
            }
            
            if (opacity < 1) {
              ctx.globalAlpha = opacity;
            }
            ctx.drawImage(img, x - drawWidth/2, y - drawHeight/2, drawWidth, drawHeight);
            if (opacity < 1) {
              ctx.globalAlpha = 1;
            }
          };

          // 다리 파츠만 가로로 30% 늘려서 그리는 헬퍼 함수 (기존 10% + 추가 20%)
          const drawLegPartWithSize = (img, x, y, maxSize, opacity = 1) => {
            if (!img || !img.complete) return;
            
            const aspectRatio = img.width / img.height;
            let drawWidth, drawHeight;
            
            if (aspectRatio > 1) {
              drawWidth = maxSize * 1.3; // 가로 30% 늘리기 (기존 10% + 추가 20%)
              drawHeight = maxSize / aspectRatio;
            } else {
              drawHeight = maxSize;
              drawWidth = maxSize * aspectRatio * 1.3; // 가로 30% 늘리기 (기존 10% + 추가 20%)
            }
            
            if (opacity < 1) {
              ctx.globalAlpha = opacity;
            }
            ctx.drawImage(img, x - drawWidth/2, y - drawHeight/2, drawWidth, drawHeight);
            if (opacity < 1) {
              ctx.globalAlpha = 1;
            }
          };

          // 몸 파츠만 크기 조정하여 그리는 헬퍼 함수
          const drawBodyPartWithSize = (img, x, y, maxSize, opacity = 1) => {
            if (!img || !img.complete) return;
            
            // 몸 파츠는 50% 크게
            const bodySize = maxSize * 1.5;
            
            const aspectRatio = img.width / img.height;
            let drawWidth, drawHeight;
            
            if (aspectRatio > 1) {
              drawWidth = bodySize * 1.3; // 가로 30% 늘리기 (기존 10% + 추가 20%)
              drawHeight = (bodySize / aspectRatio) * 1.1; // 세로 10% 늘리기
            } else {
              drawHeight = bodySize * 1.1; // 세로 10% 늘리기
              drawWidth = bodySize * aspectRatio * 1.3; // 가로 30% 늘리기 (기존 10% + 추가 20%)
            }
            
            if (opacity < 1) {
              ctx.globalAlpha = opacity;
            }
            ctx.drawImage(img, x - drawWidth/2, y - drawHeight/2, drawWidth, drawHeight);
            if (opacity < 1) {
              ctx.globalAlpha = 1;
            }
          };
          
          // 튜토리얼 모드에서는 중앙 슬롯을 상태에 따라 그리기
          if (isTutorialStage) {
            // 중앙 슬롯들 그리기
            const slots = [
              { x: headSlotX, y: headSlotY, state: tutorialState.centerSlots[0] },
              { x: bodySlotX, y: bodySlotY, state: tutorialState.centerSlots[1] },
              { x: legSlotX, y: legSlotY, state: tutorialState.centerSlots[2] }
            ];
            
            slots.forEach((slot, index) => {
              if (slot.state === 0) {
                // 빈 슬롯 - Empty 이미지 그리기
                const emptyImages = [imagesRef.current.robotHeadEmpty, imagesRef.current.robotBodyEmpty, imagesRef.current.robotLegEmpty];
                if (index === 1) { // 몸체
                  drawBodyPartWithSize(emptyImages[index], slot.x, slot.y, maxPartSize);
                } else if (index === 2) { // 다리
                  drawLegPartWithSize(emptyImages[index], slot.x, slot.y, maxPartSize);
                } else { // 머리
                  drawImageWithAspectRatio(emptyImages[index], slot.x, slot.y, maxPartSize);
                }
                

              } else {
                // 채워진 슬롯 - 해당 파츠 이미지 그리기
                const partImages = [imagesRef.current.robotHead, imagesRef.current.robotBody, imagesRef.current.robotLeg];
                if (index === 1) { // 몸체
                  drawBodyPartWithSize(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                } else if (index === 2) { // 다리
                  drawLegPartWithSize(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                } else { // 머리
                  drawImageWithAspectRatio(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                }
              }
            });
            
            // 화살표 그리기 (타겟 빈 슬롯에만)
          if (imagesRef.current.robotArrow?.complete) {
              const targetSlot = tutorialState.targetEmptySlot;
              const arrowPositions = [
                { x: headSlotX, y: headSlotY },
                { x: bodySlotX, y: bodySlotY },
                { x: legSlotX, y: legSlotY }
              ];
              
              if (tutorialState.centerSlots[targetSlot] === 0) {
                const pos = arrowPositions[targetSlot];
            ctx.drawImage(imagesRef.current.robotArrow, 
                  pos.x + arrowOffsetX - arrowSize/2, pos.y + arrowOffsetY - arrowSize/2, arrowSize, arrowSize);
              }
            }
            
            // 하단에 드래그 가능한 파츠들 그리기
            const bottomY = canvas.height * 0.82;
            const horizontalSpacing = maxPartSize * 2.0; // 간격을 1.5에서 2.0으로 증가 (33% 더 넓게)
            const bottomPositions = [
              { x: centerX - horizontalSpacing, y: bottomY, type: 1 }, // 머리
              { x: centerX, y: bottomY, type: 2 },                    // 몸체
              { x: centerX + horizontalSpacing, y: bottomY, type: 3 } // 다리
            ];
            
            bottomPositions.forEach((pos) => {
              const targetSlot = tutorialState.targetEmptySlot;
              const requiredPartType = targetSlot + 1;
              
              // 드래그 중인 파츠인지 확인
              const isDragged = tutorialState.isDragging && tutorialState.draggedPart === pos.type;
              
              if (pos.type === requiredPartType && !isDragged) {
                // 드래그 가능한 파츠 (밝게 표시)
                const partImages = [imagesRef.current.robotHead, imagesRef.current.robotBody, imagesRef.current.robotLeg];
                if (pos.type === 2) { // 몸체
                  drawBodyPartWithSize(partImages[pos.type - 1], pos.x, pos.y, maxPartSize);
                  // 드래그 가능함을 나타내는 테두리 (몸체 실제 크기에 맞춤)
                  const bodySize = maxPartSize * 1.5;
                  const borderWidth = bodySize * 1.3; // 가로 30% 늘어난 크기
                  const borderHeight = bodySize * 1.1; // 세로 10% 늘어난 크기
                  ctx.strokeStyle = '#00FF00';
                  ctx.lineWidth = 2;
                  ctx.strokeRect(pos.x - borderWidth/2, pos.y - borderHeight/2, borderWidth, borderHeight);
                } else if (pos.type === 3) { // 다리
                  drawLegPartWithSize(partImages[pos.type - 1], pos.x, pos.y, maxPartSize);
                  // 드래그 가능함을 나타내는 테두리 (다리 실제 크기에 맞춤)
                  const borderWidth = maxPartSize * 1.3; // 가로 30% 늘어난 크기
                  const borderHeight = maxPartSize; // 세로는 기본 크기
                  ctx.strokeStyle = '#00FF00';
                  ctx.lineWidth = 2;
                  ctx.strokeRect(pos.x - borderWidth/2, pos.y - borderHeight/2, borderWidth, borderHeight);
                } else { // 머리 (pos.type === 1)
                  drawImageWithAspectRatio(partImages[pos.type - 1], pos.x, pos.y, maxPartSize);
                  // 드래그 가능함을 나타내는 테두리 (머리는 기본 크기)
                  ctx.strokeStyle = '#00FF00';
                  ctx.lineWidth = 2;
                  ctx.strokeRect(pos.x - maxPartSize/2, pos.y - maxPartSize/2, maxPartSize, maxPartSize);
                }
              } else if (!isDragged) {
                // 드래그 불가능한 파츠 (반투명)
                const partImages = [imagesRef.current.robotHead, imagesRef.current.robotBody, imagesRef.current.robotLeg];
                if (pos.type === 2) { // 몸체
                  drawBodyPartWithSize(partImages[pos.type - 1], pos.x, pos.y, maxPartSize, 0.3);
                } else if (pos.type === 3) { // 다리
                  drawLegPartWithSize(partImages[pos.type - 1], pos.x, pos.y, maxPartSize, 0.3);
                } else { // 머리
                  drawImageWithAspectRatio(partImages[pos.type - 1], pos.x, pos.y, maxPartSize, 0.3);
                }
              }
            });
            
            // 드래그 중인 파츠 그리기
            if (tutorialState.isDragging && tutorialState.draggedPart) {
              const partImages = [imagesRef.current.robotHead, imagesRef.current.robotBody, imagesRef.current.robotLeg];
              const dragX = mousePos.x - tutorialState.dragOffset.x;
              const dragY = mousePos.y - tutorialState.dragOffset.y;
              
              if (tutorialState.draggedPart === 2) { // 몸체
                drawBodyPartWithSize(partImages[tutorialState.draggedPart - 1], dragX, dragY, maxPartSize, 0.8);
              } else if (tutorialState.draggedPart === 3) { // 다리
                drawLegPartWithSize(partImages[tutorialState.draggedPart - 1], dragX, dragY, maxPartSize, 0.8);
              } else { // 머리
                drawImageWithAspectRatio(partImages[tutorialState.draggedPart - 1], dragX, dragY, maxPartSize, 0.8);
              }
            }
          } else if (!isTutorialStage) {
            // 퀴즈 모드
            // 중앙 슬롯들 그리기 - 머리와 다리는 채워진 상태, 몸체만 빈자리
            const slots = [
              { x: headSlotX, y: headSlotY, state: quizState.centerSlots[0] }, // 머리 (채워짐)
              { x: bodySlotX, y: bodySlotY, state: quizState.centerSlots[1] },  // 몸체 (빈자리)
              { x: legSlotX, y: legSlotY, state: quizState.centerSlots[2] }     // 다리 (채워짐)
            ];
            
            slots.forEach((slot, index) => {
              if (slot.state === 0) {
                // 빈 슬롯 (몸체) - Empty 이미지 그리기
                if (index === 1) { // 몸체
                  drawBodyPartWithSize(imagesRef.current.robotBodyEmpty, slot.x, slot.y, maxPartSize);
                } else {
                  drawImageWithAspectRatio(imagesRef.current.robotBodyEmpty, slot.x, slot.y, maxPartSize);
                }
              } else {
                // 채워진 슬롯 - 해당 파츠 이미지 그리기
                const partImages = [imagesRef.current.robotHead, imagesRef.current.robotBody, imagesRef.current.robotLeg];
                if (index === 1) { // 몸체
                  drawBodyPartWithSize(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                } else if (index === 2) { // 다리
                  drawLegPartWithSize(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                } else { // 머리
                  drawImageWithAspectRatio(partImages[slot.state - 1], slot.x, slot.y, maxPartSize);
                }
              }
            });
            
            // 화살표 그리기 (몸체 슬롯이 비어있을 때만)
            if (imagesRef.current.robotArrow?.complete && quizState.centerSlots[1] === 0) {
            ctx.drawImage(imagesRef.current.robotArrow, 
                bodySlotX + arrowOffsetX - arrowSize/2, bodySlotY + arrowOffsetY - arrowSize/2, arrowSize, arrowSize);
            }
            
            // 하단에 4개의 퀴즈 선택지 그리기 (2x2 격자) - 선택지 크기를 빈 몸통 파츠와 동일하게
            const bottomAreaY = canvas.height * 0.68; // 하단 여백 확보를 위해 위로 올림
            const optionSize = maxPartSize * 1.5; // 빈 몸통 파츠와 동일한 크기 (1.5배)
            const horizontalSpacing = maxPartSize * 2.2; // 가로 간격 확대 (1.65 -> 2.2)
            const verticalSpacing = horizontalSpacing * 0.6; // 세로 간격을 가로 간격의 60%로 조정
            
            const positions = [
              { x: centerX - horizontalSpacing/2, y: bottomAreaY, index: 0 },                            // 왼쪽 상단
              { x: centerX + horizontalSpacing/2, y: bottomAreaY, index: 1 },                            // 오른쪽 상단
              { x: centerX - horizontalSpacing/2, y: bottomAreaY + verticalSpacing, index: 2 },          // 왼쪽 하단
              { x: centerX + horizontalSpacing/2, y: bottomAreaY + verticalSpacing, index: 3 }           // 오른쪽 하단
            ];
            
            positions.forEach((pos) => {
              // 드래그 중인 선택지인지 확인
              const isDragged = quizState.isDragging && quizState.draggedOption === pos.index;
              // 몸체 슬롯이 채워졌는지 확인
              const isGameCompleted = quizState.centerSlots[1] !== 0;
              
              if (!isDragged) {
                // RobotBodyQuiz 이미지 그리기 (게임 완료 시 반투명) - 빈 몸통 파츠와 동일한 크기로
                if (imagesRef.current.robotBodyQuiz?.complete) {
                  const opacity = isGameCompleted ? 0.5 : 1;
                  if (opacity < 1) ctx.globalAlpha = opacity;
                  drawBodyPartWithSize(imagesRef.current.robotBodyQuiz, pos.x, pos.y, maxPartSize, opacity);
                  if (opacity < 1) ctx.globalAlpha = 1;
                }
                
                // 선택지 텍스트 그리기 (게임 완료 시 회색)
                ctx.fillStyle = isGameCompleted ? '#888888' : '#000000';
                const fontSize = Math.max(12, Math.round(optionSize * 0.15));
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                // 텍스트 줄바꿈 처리
                const optionText = quizState.options[pos.index] || `선택지 ${pos.index + 1}`;
                const maxWidth = optionSize * 0.8;
                const words = optionText.split(' ');
                let lines = [];
                let currentLine = '';
                
                words.forEach(word => {
                  const testLine = currentLine + (currentLine ? ' ' : '') + word;
                  const metrics = ctx.measureText(testLine);
                  if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    currentLine = testLine;
                  }
                });
                if (currentLine) lines.push(currentLine);
                
                // 최대 2줄까지만 표시
                lines = lines.slice(0, 2);
                const lineHeight = fontSize * 1.2;
                // 텍스트를 반 줄만 아래로 이동 (lineHeight의 절반만큼 아래로)
                const totalHeight = lines.length * lineHeight;
                const startY = pos.y - totalHeight / 2 + lineHeight / 2 + lineHeight * 0.5; // lineHeight의 50%만 아래로
                
                lines.forEach((line, lineIndex) => {
                  ctx.fillText(line, pos.x, startY + lineIndex * lineHeight);
                });
                
                // 선택된 정답에 표시 (게임 완료 후)
                if (isGameCompleted && pos.index === quizState.selectedIndex) {
                  // 선택된 답안에 특별한 표시 (실제 그려진 이미지 크기에 맞춤)
                  const isCorrect = quizState.selectedIndex === quizState.correctIndex;
                  // drawBodyPartWithSize로 그려진 실제 크기 계산 (몸체는 50% 크게)
                  const img = imagesRef.current.robotBodyQuiz;
                  if (img && img.complete) {
                    const bodySize = maxPartSize * 1.5; // drawBodyPartWithSize와 동일한 크기
                    const aspectRatio = img.width / img.height;
                    let borderWidth, borderHeight;
                    if (aspectRatio > 1) {
                      borderWidth = bodySize * 1.3; // 가로 30% 늘어난 크기
                      borderHeight = (bodySize / aspectRatio) * 1.1; // 세로 10% 늘어난 크기
                    } else {
                      borderHeight = bodySize * 1.1; // 세로 10% 늘어난 크기
                      borderWidth = bodySize * aspectRatio * 1.3; // 가로 30% 늘어난 크기
                    }
                    ctx.strokeStyle = isCorrect ? '#00FF00' : '#FF0000';
                    ctx.lineWidth = 3;
                    ctx.strokeRect(pos.x - borderWidth/2, pos.y - borderHeight/2, borderWidth, borderHeight);
                  }
                } else if (isGameCompleted && pos.index === quizState.correctIndex && quizState.selectedIndex !== quizState.correctIndex) {
                  // 정답에 표시 (오답을 선택했을 경우) (실제 그려진 이미지 크기에 맞춤)
                  const img = imagesRef.current.robotBodyQuiz;
                  if (img && img.complete) {
                    const bodySize = maxPartSize * 1.5; // drawBodyPartWithSize와 동일한 크기
                    const aspectRatio = img.width / img.height;
                    let borderWidth, borderHeight;
                    if (aspectRatio > 1) {
                      borderWidth = bodySize * 1.3; // 가로 30% 늘어난 크기
                      borderHeight = (bodySize / aspectRatio) * 1.1; // 세로 10% 늘어난 크기
                    } else {
                      borderHeight = bodySize * 1.1; // 세로 10% 늘어난 크기
                      borderWidth = bodySize * aspectRatio * 1.3; // 가로 30% 늘어난 크기
                    }
                    ctx.strokeStyle = '#00FF00';
                    ctx.lineWidth = 3;
                    ctx.setLineDash([5, 5]);
                    ctx.strokeRect(pos.x - borderWidth/2, pos.y - borderHeight/2, borderWidth, borderHeight);
                    ctx.setLineDash([]);
                  }
                }
              }
            });
            
            // 드래그 중인 선택지 그리기
            if (quizState.isDragging && quizState.draggedOption !== null) {
              const dragX = mousePos.x - quizState.dragOffset.x;
              const dragY = mousePos.y - quizState.dragOffset.y;
              
              // 반투명 RobotBodyQuiz 이미지 - 빈 몸통 파츠와 동일한 크기로
              if (imagesRef.current.robotBodyQuiz?.complete) {
                drawBodyPartWithSize(imagesRef.current.robotBodyQuiz, dragX, dragY, maxPartSize, 0.8);
              }
              
              // 드래그 중인 선택지 텍스트
              ctx.fillStyle = '#000000';
              const fontSize = Math.max(12, Math.round(optionSize * 0.15));
              ctx.font = `bold ${fontSize}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              const optionText = quizState.options[quizState.draggedOption] || `선택지 ${quizState.draggedOption + 1}`;
              ctx.fillText(optionText, dragX, dragY);
            }
          } else {
            // 기본 모드 (이미지 로딩 완료 전 등)
            drawImageWithAspectRatio(imagesRef.current.robotHeadEmpty, headSlotX, headSlotY, maxPartSize);
            drawBodyPartWithSize(imagesRef.current.robotBodyEmpty, bodySlotX, bodySlotY, maxPartSize);
            drawLegPartWithSize(imagesRef.current.robotLegEmpty, legSlotX, legSlotY, maxPartSize);
            
            // 화살표 그리기 (각 파츠 오른쪽에)
            if (imagesRef.current.robotArrow?.complete) {
              ctx.drawImage(imagesRef.current.robotArrow, 
                headSlotX + arrowOffsetX - arrowSize/2, headSlotY + arrowOffsetY - arrowSize/2, arrowSize, arrowSize);
              ctx.drawImage(imagesRef.current.robotArrow, 
                bodySlotX + arrowOffsetX - arrowSize/2, bodySlotY + arrowOffsetY - arrowSize/2, arrowSize, arrowSize);
            ctx.drawImage(imagesRef.current.robotArrow, 
                legSlotX + arrowOffsetX - arrowSize/2, legSlotY + arrowOffsetY - arrowSize/2, arrowSize, arrowSize);
            }
            
            // 하단에 실제 로봇 파츠 이미지를 가로로 배치
            const bottomY = canvas.height * 0.82;
            const horizontalSpacing = maxPartSize * 2.0; // 튜토리얼과 동일한 간격
          const bottomHeadX = centerX - horizontalSpacing;
          const bottomBodyX = centerX;
          const bottomLegX = centerX + horizontalSpacing;
          
          drawImageWithAspectRatio(imagesRef.current.robotHead, bottomHeadX, bottomY, maxPartSize);
            drawBodyPartWithSize(imagesRef.current.robotBody, bottomBodyX, bottomY, maxPartSize);
          drawLegPartWithSize(imagesRef.current.robotLeg, bottomLegX, bottomY, maxPartSize);
          }
        } else {
          // 이미지 로딩 중 표시
          ctx.fillStyle = '#E0E7FF';
          const subtitleFontSize = Math.max(16, Math.min(24, canvas.width * 0.03));
          ctx.font = `${subtitleFontSize}px Arial`;
          ctx.fillText('(이미지 로딩 중...)', centerX, centerY);
        }
      }

      // 피드백 오버레이 그리기
      if (stagePhase === 'feedback' && feedbackActive && imagesRef.current.ready) {
        ctx.fillStyle = 'rgba(107, 114, 128, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const img = feedbackIsSuccess ? imagesRef.current.success : imagesRef.current.fail;
        if (img && img.complete) {
          const size = Math.min(canvas.width, canvas.height) * 0.3;
          const cx = canvas.width / 2;
          const cy = canvas.height / 2;
          ctx.drawImage(img, cx - size / 2, cy - size / 2, size, size);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    
    draw();

    // 클리너 함수
    // 이벤트 리스너 추가 (튜토리얼 모드와 퀴즈 모드)
    if (stagePhase === 'play') {
      canvas.addEventListener('mousedown', handlePointerDown);
      canvas.addEventListener('mousemove', handlePointerMove);
      canvas.addEventListener('mouseup', handlePointerUp);
      canvas.addEventListener('touchstart', handlePointerDown, { passive: false });
      canvas.addEventListener('touchmove', handlePointerMove, { passive: false });
      canvas.addEventListener('touchend', handlePointerUp, { passive: false });
      // 터치 취소 이벤트도 처리하여 드래그 상태를 리셋
      canvas.addEventListener('touchcancel', handlePointerUp, { passive: false });
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      
      // 이벤트 리스너 제거
      if (canvas) {
        canvas.removeEventListener('mousedown', handlePointerDown);
        canvas.removeEventListener('mousemove', handlePointerMove);
        canvas.removeEventListener('mouseup', handlePointerUp);
        canvas.removeEventListener('touchstart', handlePointerDown);
        canvas.removeEventListener('touchmove', handlePointerMove);
        canvas.removeEventListener('touchend', handlePointerUp);
        canvas.removeEventListener('touchcancel', handlePointerUp);
      }
    };
  }, [stagePhase, localStage, quiz?.questions, feedbackActive, feedbackIsSuccess, tutorialState, quizState, mousePos, isTutorialStage, includeTutorial]);

  // 게임 로직 처리
  useEffect(() => {
    if (stagePhase !== 'play') return;

    // 튜토리얼 단계에서는 드래그 앤 드롭으로만 완료
    // 자동 완료 타이머는 제거하고, 드래그 앤 드롭 완료 시에만 성공 처리
    if (isTutorialStage) {
      // 튜토리얼은 드래그 앤 드롭으로만 완료되므로 타이머 없음
      return;
    }

    // 퀴즈 단계에서는 사용자 입력으로만 완료됨 (자동 타이머 제거)
  }, [stagePhase, localStage, quiz?.questions, correctCountRef, answersRef, wrongIndexesRef, stageAnsweredRef, setStagePhase, setFeedbackActive, setFeedbackIsSuccess, setFeedbackRect, setIsGameInProgress, isTutorialStage]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#237BFF' // 로봇 팩토리 테마 색상
    }}>
      {/* 게임 캔버스 */}
      <canvas 
        ref={canvasRef} 
        style={{ 
          width: '100%',
          height: '100%',
          touchAction: 'none', 
          WebkitTapHighlightColor: 'transparent', 
          pointerEvents: stagePhase === 'play' ? 'auto' : 'none',
          display: 'block',
          cursor: stagePhase === 'play' ? 'pointer' : 'default'
        }} 
      />
    </div>
  );
};

export default ShortFormQuizRobotFactory;
